import { FC, type ReactNode, useMemo } from 'react'

import {
  AcknowledgmentSignerPickerDialog,
  type AcknowledgmentSignerPickerIntent,
  type AcknowledgmentSignerPickerMode,
  type AcknowledgmentSignerSelectionPayload,
} from 'partner360/features/acknowledgmentSignerPicker'

import { CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem } from '../model/types'

type AcknowledgmentSignerActionDialogProps = {
  show: boolean
  commitmentType: CommitmentTypes | null
  intent: AcknowledgmentSignerPickerIntent
  /** Another type's picker than the commitment type's own, e.g. the Manager form for a PBM. */
  mode?: AcknowledgmentSignerPickerMode
  /** Replaces the title the picker's mode and intent give. */
  title?: ReactNode
  partnerId: string
  item?: AcknowledgmentCommitmentItem | null
  excludedEmails?: string[]
  canAddOptional?: boolean
  isSubmitting?: boolean
  onClose: () => void
  onApply: (data: AcknowledgmentSignerSelectionPayload) => void | Promise<void>
}

const commitmentTypeToMode: Record<CommitmentTypes, AcknowledgmentSignerPickerMode> = {
  [CommitmentTypes.Manager]: 'manager',
  [CommitmentTypes.PBM]: 'pbm',
  [CommitmentTypes.Partner]: 'partner',
}

const mapInitialSelection = (
  item?: AcknowledgmentCommitmentItem | null
): Partial<AcknowledgmentSignerSelectionPayload> | undefined => {
  if (!item) {
    return undefined
  }

  return {
    isOptional: item.IsOptional,
    email: item.SignerEmail ?? '',
    firstName: item.SignerFirstName ?? '',
    lastName: item.SignerLastName ?? '',
    role: item.SignerRole ?? undefined,
    contactId: item.ContactId ? String(item.ContactId) : undefined,
  }
}

export const AcknowledgmentSignerActionDialog: FC<AcknowledgmentSignerActionDialogProps> = ({
  show,
  commitmentType,
  intent,
  mode,
  title,
  partnerId,
  item,
  excludedEmails,
  isSubmitting,
  canAddOptional,
  onClose,
  onApply,
}) => {
  const initialSelection = useMemo(() => mapInitialSelection(item), [item])

  if (!commitmentType) {
    return null
  }

  return (
    <AcknowledgmentSignerPickerDialog
      mode={mode ?? commitmentTypeToMode[commitmentType]}
      title={title}
      intent={intent}
      show={show}
      partnerId={partnerId}
      initialSelection={initialSelection}
      excludedEmails={excludedEmails}
      isSubmitting={isSubmitting}
      canAddOptional={canAddOptional}
      onClose={onClose}
      onSelect={async (data) => {
        await onApply(data)
      }}
    />
  )
}

type AddPartnerDialogProps = {
  show: boolean
  commitmentType: CommitmentTypes | null
  partnerId: string
  onClose: () => void
  onApply: (data: AcknowledgmentSignerSelectionPayload) => void | Promise<void>
}

export const AddPartnerDialog: FC<AddPartnerDialogProps> = (props) => {
  return <AcknowledgmentSignerActionDialog {...props} intent="add" />
}
